import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { useGetCustomersQuery } from '@/store/api/services/customers/customers';
import { useCreateCouponMutation } from '@/store/api/services/marketing/coupons';
import type { CouponType, CreateCouponRequest, Customer } from '@/types';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

const COUPON_SVG_MAX_SIZE_MB = 2;

const CreateCoupon = ({ hide }: ModalChildrenProps) => {
  const [createCoupon, { isLoading }] = useCreateCouponMutation();

  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [couponType, setCouponType] = useState<CouponType>('global_coupon');
  const [isRegisterInMyAccount, setIsRegisterInMyAccount] = useState<boolean | null>(null);
  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState('');
  const [autoAssignOnCheck, setAutoAssignOnCheck] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [usageLimit, setUsageLimit] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isIndividual = couponType === 'individual_coupon';
  const needsCustomerIds = isIndividual && isRegisterInMyAccount === false && !autoAssignOnCheck;
  const isDateOrderValid = !startDate || !expiryDate || startDate < expiryDate;

  // Debounce the customer search (300ms), matching AssignedUserSelect.tsx / LocationSelect.tsx
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedCustomerSearch(customerSearch), 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  const { data: customersData, isFetching: isCustomersFetching } = useGetCustomersQuery(
    {
      search_text: debouncedCustomerSearch,
      pagination: {
        current_page: 1,
        items_per_page: 25,
        sort_ascending: true,
        sort_by: 'full_name',
      },
    },
    { skip: !needsCustomerIds },
  );

  const customerOptions = [
    ...selectedCustomers,
    ...(customersData?.data ?? []).filter(
      (customer) => !selectedCustomers.some((selected) => selected.id === customer.id),
    ),
  ];

  const isValid =
    code.trim().length > 0 &&
    discountValue !== '' &&
    Number(discountValue) >= 1 &&
    Number(discountValue) <= 99 &&
    isDateOrderValid &&
    (!isIndividual || isRegisterInMyAccount !== null) &&
    (!needsCustomerIds || selectedCustomers.length > 0);

  const handleSubmit = async () => {
    const payload: CreateCouponRequest = {
      code: code.trim(),
      discount_type: 'percentage',
      discount_value: Number(discountValue),
      coupon_type: couponType,
      title: title.trim() || undefined,
      description: description.trim() || undefined,
      file: imageFile ?? undefined,
      start_date: startDate ? new Date(startDate).toISOString() : null,
      expiry_date: expiryDate ? new Date(expiryDate).toISOString() : null,
      usage_limit: usageLimit ? Number(usageLimit) : null,
    };

    if (isIndividual) {
      payload.is_register_in_my_account = isRegisterInMyAccount ?? false;
      if (!isRegisterInMyAccount) {
        payload.auto_assign_on_check = autoAssignOnCheck;
        if (!autoAssignOnCheck) {
          payload.customer_ids = selectedCustomers.map((customer) => customer.id);
        }
      }
    }

    try {
      await createCoupon(payload).unwrap();
      toast.success('Coupon created successfully');
      hide();
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to create coupon';
      toast.error(errorMessage);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
    if (!isSvg) {
      setImageError('Only SVG files are accepted');
      return;
    }

    if (file.size > COUPON_SVG_MAX_SIZE_MB * 1024 * 1024) {
      setImageError(`File size can not exceed ${COUPON_SVG_MAX_SIZE_MB}MB`);
      return;
    }

    setImageError('');
    setImageFile(file);
  };

  const handleFileRemove = () => {
    setImageFile(null);
    setImageError('');
  };

  return (
    <>
      <DialogContent>
        <Stack gap={3} py={2}>
          <TextField
            label="Code *"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. SUMMER25"
            fullWidth
            inputProps={{ style: { textTransform: 'uppercase' }, 'data-test': 'coupon-code-input' }}
          />

          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            inputProps={{ 'data-test': 'coupon-title-input' }}
          />

          <Stack gap={1}>
            <Typography variant="body2" color="text.secondary">
              Description
            </Typography>
            <TextField
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Add a description for this coupon..."
              inputProps={{ 'data-test': 'coupon-description-input' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '90px',
                },
              }}
            />
          </Stack>

          <TextField
            label="Discount Percentage (1–99) *"
            type="number"
            value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
            inputProps={{ min: 1, max: 99, 'data-test': 'coupon-discount-value-input' }}
            fullWidth
            helperText="e.g. 25 → 25% off. Fixed dollar-amount discounts aren't supported yet."
          />

          <FormControl fullWidth>
            <InputLabel>Coupon Type *</InputLabel>
            <Select
              value={couponType}
              label="Coupon Type *"
              onChange={(e: SelectChangeEvent) => {
                setCouponType(e.target.value as CouponType);
                setIsRegisterInMyAccount(null);
                setSelectedCustomers([]);
                setCustomerSearch('');
                setAutoAssignOnCheck(false);
              }}
            >
              <MenuItem value="global_coupon">Global</MenuItem>
              <MenuItem value="individual_coupon">Individual</MenuItem>
            </Select>
          </FormControl>

          {isIndividual && (
            <FormControl>
              <FormLabel>Assign to all registered Cognito users?</FormLabel>
              <RadioGroup
                row
                value={isRegisterInMyAccount === null ? '' : String(isRegisterInMyAccount)}
                onChange={(e) => setIsRegisterInMyAccount(e.target.value === 'true')}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Yes (async, all Cognito users)"
                />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          )}

          {isIndividual && isRegisterInMyAccount === false && (
            <FormControlLabel
              control={
                <Switch
                  checked={autoAssignOnCheck}
                  onChange={(e) => {
                    setAutoAssignOnCheck(e.target.checked);
                    if (e.target.checked) setSelectedCustomers([]);
                  }}
                />
              }
              label="Auto-assign when a customer meets criteria, instead of specific customer IDs"
            />
          )}

          {needsCustomerIds && (
            <Stack gap={1}>
              <Autocomplete
                multiple
                size="small"
                options={customerOptions}
                loading={isCustomersFetching}
                value={selectedCustomers}
                getOptionLabel={(customer) => `${customer.full_name} (#${customer.id})`}
                isOptionEqualToValue={(option, val) => option.id === val.id}
                onChange={(_, newValue) => setSelectedCustomers(newValue)}
                onInputChange={(_, newValue) => setCustomerSearch(newValue)}
                renderTags={() => null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customers *"
                    placeholder={selectedCustomers.length ? '' : 'Search customers by name...'}
                    helperText="Search and select the customers this coupon is assigned to"
                  />
                )}
              />
              {selectedCustomers.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selectedCustomers.map((customer) => (
                    <Chip
                      key={customer.id}
                      size="small"
                      label={`${customer.full_name} (#${customer.id})`}
                      onDelete={() =>
                        setSelectedCustomers((prev) => prev.filter((c) => c.id !== customer.id))
                      }
                    />
                  ))}
                </Box>
              )}
            </Stack>
          )}

          <Divider />

          <Typography variant="subtitle2" color="text.secondary">
            Optional settings
          </Typography>

          <Stack direction="row" gap={2}>
            <TextField
              label="Start Date & Time"
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              error={!isDateOrderValid}
              helperText={
                !isDateOrderValid ? 'Must be before the expiry date' : 'Leave empty to start now'
              }
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Expiry Date & Time"
              type="datetime-local"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <TextField
            label="Usage Limit"
            type="number"
            value={usageLimit}
            onChange={(e) => setUsageLimit(e.target.value)}
            inputProps={{ min: 1 }}
            fullWidth
            helperText="Leave empty for unlimited"
          />

          <Stack gap={1}>
            <Typography variant="body2">Image</Typography>

            <input
              ref={fileInputRef}
              type="file"
              accept=".svg,image/svg+xml"
              hidden
              onChange={handleFileSelect}
            />

            {imageFile ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography variant="body2" noWrap>
                  {imageFile.name}
                </Typography>
                <IconButton size="small" onClick={handleFileRemove} data-test="coupon-image-remove">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Paper>
            ) : (
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                data-test="coupon-image-upload"
              >
                Upload SVG
              </Button>
            )}

            {imageError && <FormHelperText error>{imageError}</FormHelperText>}
          </Stack>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={hide}>Cancel</Button>
        <LoadingButton
          variant="contained"
          loading={isLoading}
          disabled={!isValid}
          onClick={handleSubmit}
        >
          Create Coupon
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default CreateCoupon;
